import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, ArrowUpRight, Eye } from "lucide-react";
import SEO from "@/components/SEO";
import Comments from "@/components/Comments";
import ShareButtons from "@/components/ShareButtons";
import { fetchPublicPost, fetchPublicPosts, fetchPreviewPost } from "@/lib/api";
import { BOOKING_URL } from "@/lib/constants";
import { absoluteUrl } from "@/lib/seo-config";
import { track } from "@/lib/analytics";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogPost({ previewMode = false }) {
  const params = useParams();
  const slug = params.slug;
  const token = params.token;
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const [allPosts, setAllPosts] = useState([]);

  useEffect(() => {
    setError(false);
    setPost(null);
    const loader = previewMode
      ? fetchPreviewPost(token)
      : fetchPublicPost(slug);
    loader.then(setPost).catch(() => setError(true));
  }, [slug, token, previewMode]);

  // Fetch the full public catalogue once for Related Reading.
  useEffect(() => {
    if (previewMode) return; // No related reading in preview — focus on the draft.
    fetchPublicPosts()
      .then((rows) => setAllPosts(Array.isArray(rows) ? rows : []))
      .catch(() => setAllPosts([]));
  }, [previewMode]);

  // Related posts: same category first, then any others, capped at 3.
  const related = useMemo(() => {
    if (!post || allPosts.length === 0) return [];
    const others = allPosts.filter((p) => p.slug !== post.slug);
    const sameCat = others.filter((p) => p.category === post.category);
    const fillers = others.filter((p) => p.category !== post.category);
    return [...sameCat, ...fillers].slice(0, 3);
  }, [post, allPosts]);

  // Engaged read: fire once when user has been on page 30s OR scrolled past 60%
  useEffect(() => {
    if (!post || previewMode) return; // Don't pollute analytics with preview sessions.
    let fired = false;
    const fire = (trigger) => {
      if (fired) return;
      fired = true;
      track("blog_engaged_read", {
        slug: post.slug,
        title: post.title,
        category: post.category,
        trigger,
      });
    };
    const timer = setTimeout(() => fire("time_30s"), 30000);
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled =
        (h.scrollTop + window.innerHeight) /
        (h.scrollHeight || window.innerHeight);
      if (scrolled >= 0.6) fire("scroll_60");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [post]);

  if (error) {
    return (
      <main className="bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-32">
          <h1 className="text-4xl font-bold tracking-tight text-[#080808]">
            Post not found.
          </h1>
          <Link to="/blog" className="ash-link-underline mt-8 inline-flex">
            <ArrowLeft size={14} /> Back to all posts
          </Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-[#3C4048]">
          Loading…
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page" className="bg-white">
      <SEO
        title={post.seo_title || post.title}
        description={post.meta_description || post.excerpt}
        path={previewMode ? `/blog/preview/${token}` : `/blog/${post.slug}`}
        image={post.featured_image_url}
        type="article"
        noIndex={previewMode}
        article={{
          headline: post.title,
          description: post.meta_description || post.excerpt,
          image: post.featured_image_url,
          datePublished: post.published_at,
          dateModified: post.updated_at || post.published_at,
          slug: post.slug,
          category: post.category,
        }}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
              { "@type": "ListItem", position: 2, name: "Journal", item: absoluteUrl("/blog") },
              { "@type": "ListItem", position: 3, name: post.title, item: absoluteUrl(`/blog/${post.slug}`) },
            ],
          },
          ...(post.faqs && post.faqs.length > 0
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: post.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: { "@type": "Answer", text: f.answer },
                  })),
                },
              ]
            : []),
        ]}
      />
      {previewMode && (
        <div
          data-testid="preview-banner"
          className="bg-[#080808] text-white text-xs uppercase tracking-[0.2em] font-semibold px-6 py-3 flex flex-wrap items-center justify-center gap-3"
        >
          <Eye size={14} />
          Preview Mode — Status: {post.status || "Draft"} · Not indexed by search engines · Share this link only with trusted reviewers.
        </div>
      )}
      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12">
          <Link
            to="/blog"
            className="ash-link-underline inline-flex"
            data-testid="blog-back-link"
          >
            <ArrowLeft size={14} /> Blog
          </Link>
          <div className="mt-8 text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">
            {post.category}
          </div>
          <h1
            data-testid="blog-post-title"
            className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#080808]"
          >
            {post.title}
          </h1>
          <div className="mt-6 text-sm text-[#6B7280] uppercase tracking-[0.18em]">
            By Ashlan Clinic Clinical Massage Therapy Team · {formatDate(post.published_at)}
          </div>
        </div>
      </section>

      <section className="border-b border-[#e5e7eb]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
          <div className="aspect-[16/9] w-full overflow-hidden bg-[#F5F5F5]">
            <img
              src={post.featured_image_url}
              alt={post.featured_image_description || post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <article
            data-testid="blog-post-body"
            className="prose-ashlan"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content_markdown || ""}
            </ReactMarkdown>
          </article>

          {/* FAQ */}
          {post.faqs && post.faqs.length > 0 && (
            <div className="mt-16 pt-12 border-t border-[#e5e7eb]" data-testid="blog-faqs">
              <span className="ash-eyebrow">Frequently Asked</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-[#080808]">
                Questions our clients ask.
              </h2>
              <div className="mt-10 flex flex-col gap-8">
                {post.faqs.map((f, i) => (
                  <div key={f.question || i} data-testid={`blog-faq-${i}`}>
                    <h3 className="text-xl font-bold text-[#080808] leading-tight">
                      {f.question}
                    </h3>
                    <p className="mt-3 text-[#3C4048] leading-relaxed">
                      {f.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 bg-[#F5F5F5] p-10 border-l-2 border-[#5FA8B4]">
            <span className="ash-eyebrow">Next Step</span>
            <p className="mt-3 text-[#080808] text-xl leading-relaxed font-medium">
              {post.cta_primary}
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a
                href={BOOKING_URL}
                data-testid="blog-cta-book"
                className="ash-btn-primary"
              >
                Book Your Session <ArrowUpRight size={16} />
              </a>
              {post.cta_secondary && (
                <Link to="/services" className="ash-btn-secondary">
                  {post.cta_secondary}
                </Link>
              )}
            </div>
          </div>

          {/* Internal Links */}
          {post.internal_links && post.internal_links.length > 0 && (
            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3" data-testid="blog-internal-links">
              {post.internal_links.map((l, i) => (
                <Link key={l.url || i} to={l.url} className="ash-link-underline">
                  {l.label}
                </Link>
              ))}
            </div>
          )}

          {/* D5 — UTM-tagged share buttons (hidden in preview mode) */}
          {!previewMode && (
            <ShareButtons path={`/blog/${post.slug}`} title={post.title} />
          )}
        </div>
      </section>

      {/* Related Reading (A6) */}
      {!previewMode && related.length > 0 && (
        <section
          data-testid="related-reading"
          className="bg-[#F5F5F5] border-t border-[#e5e7eb] py-16 md:py-24"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <span className="ash-eyebrow">Related Reading</span>
                <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
                  Continue exploring.
                </h2>
              </div>
              <Link to="/blog" className="ash-link-underline" data-testid="related-back-to-journal">
                All journal entries <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {related.map((r, i) => (
                <Link
                  to={`/blog/${r.slug}`}
                  key={r.slug}
                  data-testid={`related-card-${i}`}
                  className="group bg-white border border-[#e5e7eb] flex flex-col transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#F5F5F5]">
                    <img
                      src={r.featured_image_url}
                      alt={r.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 flex flex-col gap-3">
                    <div className="text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">
                      {r.category}
                    </div>
                    <h3 className="text-lg font-bold text-[#080808] leading-tight">
                      {r.title}
                    </h3>
                    {r.excerpt && (
                      <p className="text-sm text-[#3C4048] leading-relaxed line-clamp-3">
                        {r.excerpt}
                      </p>
                    )}
                    <span className="ash-link-underline mt-2">
                      Read <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* A11 — Comments */}
      {!previewMode && <Comments slug={post.slug} />}
    </main>
  );
}
